/* eslint-disable @typescript-eslint/naming-convention */
export class ErrorCodes {
  // Authentication / Authorization
  static readonly UNAUTHORIZED_GENERIC = 'ERR001A';
  static readonly UNAUTHORIZED_TENANT_INVALID = 'ERR001B';
  static readonly UNAUTHORIZED_SESSION_EXPIRED = 'ERR001C';

  // Validation / Input
  static readonly INVALID_INPUT = 'ERR002';

  // Forbidden
  static readonly FORBIDDEN = 'ERR003';

  // Internal / Server
  static readonly INTERNAL_ERROR = 'ERR004';

  // Application Logic
  static readonly BAD_REQUEST = 'ERR005';
  static readonly NOT_FOUND = 'ERR006';
  static readonly DUPLICATE = 'ERR007';

  // Database
  static readonly DB_ERROR = 'ERR008';

  // External Services
  static readonly EXTERNAL_SERVICE_ERROR = 'ERR009';
}

export enum HttpStatusCode {
  OK = 200,
  Created = 201,
  BadRequest = 400,
  Unauthorized = 401,
  Forbidden = 403,
  NotFound = 404,
  Conflict = 409,
  InternalServerError = 500,
  BadGateway = 502,
  ServiceUnavailable = 503,
}

export interface ApiError {
  code?: string;
  message?: string;
  status?: number;
}

export class AppException extends Error {
  public readonly internalCode: string;
  public readonly publicCode: string;
  public readonly statusCode: HttpStatusCode;
  public readonly exposeToClient: boolean;

  constructor(
    internalCode: string,
    publicCode: string,
    message: string,
    statusCode: HttpStatusCode,
    exposeToClient: boolean = true
  ) {
    super(message);
    this.name = 'AppException';
    this.internalCode = internalCode;
    this.publicCode = publicCode;
    this.statusCode = statusCode;
    this.exposeToClient = exposeToClient;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, AppException);
    }
  }

  // AUTHENTICATION / AUTHORIZATION
  static Unauthorized(reason?: string): AppException {
    return new AppException(
      'UNAUTHORIZED',
      ErrorCodes.UNAUTHORIZED_GENERIC,
      reason ?? 'Authentication failed.',
      HttpStatusCode.Unauthorized,
      false
    );
  }

  static TenantInvalid(tenantId: number): AppException {
    return new AppException(
      'TENANT_INVALID',
      ErrorCodes.UNAUTHORIZED_TENANT_INVALID,
      `Tenant ${tenantId} not found or inactive.`,
      HttpStatusCode.Unauthorized,
      false
    );
  }

  static SessionExpired(): AppException {
    return new AppException(
      'SESSION_EXPIRED',
      ErrorCodes.UNAUTHORIZED_SESSION_EXPIRED,
      'User session expired.',
      HttpStatusCode.Unauthorized,
      false
    );
  }

  static Forbidden(reason?: string): AppException {
    return new AppException(
      'FORBIDDEN',
      ErrorCodes.FORBIDDEN,
      reason ?? 'Access denied.',
      HttpStatusCode.Forbidden,
      false
    );
  }

  // ⚙️ VALIDATION
  static InvalidParameters(details?: string): AppException {
    return new AppException(
      'INVALID_PARAMETERS',
      ErrorCodes.INVALID_INPUT,
      details ?? 'Invalid or missing parameters.',
      HttpStatusCode.BadRequest,
      true
    );
  }

  // 🧩 APPLICATION LOGIC
  static BadRequest(details?: string): AppException {
    return new AppException(
      'BAD_REQUEST',
      ErrorCodes.BAD_REQUEST,
      details ?? 'Request validation failed.',
      HttpStatusCode.BadRequest,
      true
    );
  }

  static NotFound(entityName: string): AppException {
    return new AppException(
      'NOT_FOUND',
      ErrorCodes.NOT_FOUND,
      `${entityName} not found.`,
      HttpStatusCode.NotFound,
      true
    );
  }

  static DuplicateRecord(entityName: string): AppException {
    return new AppException(
      'DUPLICATE_RECORD',
      ErrorCodes.DUPLICATE,
      `${entityName} already exists.`,
      HttpStatusCode.Conflict,
      true
    );
  }

  // 💾 DATABASE
  static DatabaseError(details?: string): AppException {
    return new AppException(
      'DATABASE_ERROR',
      ErrorCodes.DB_ERROR,
      details ?? 'Database operation failed.',
      HttpStatusCode.InternalServerError,
      false
    );
  }

  // 🌐 EXTERNAL SERVICES
  static ExternalServiceError(
    serviceName: string,
    details?: string
  ): AppException {
    return new AppException(
      'EXTERNAL_SERVICE_ERROR',
      ErrorCodes.EXTERNAL_SERVICE_ERROR,
      `${serviceName} failed. ${details ?? ''}`,
      HttpStatusCode.BadGateway,
      false
    );
  }

  // 💥 INTERNAL SERVER
  static InternalError(details?: string): AppException {
    return new AppException(
      'INTERNAL_ERROR',
      ErrorCodes.INTERNAL_ERROR,
      details ?? 'An unexpected error occurred.',
      HttpStatusCode.InternalServerError,
      false
    );
  }
}

export const handleApiError = (error: ApiError): string => {
  const code = error.code || '';
  const status = error.status;

  switch (code) {
    // 🔐 Authentication / Authorization
    case ErrorCodes.UNAUTHORIZED_GENERIC:
      return 'You are not authorized. Please log in again.';

    case ErrorCodes.UNAUTHORIZED_TENANT_INVALID:
      return 'Invalid tenant. Please contact support.';

    case ErrorCodes.UNAUTHORIZED_SESSION_EXPIRED:
      return 'Your session has expired. Please log in again.';

    // ⚙️ Validation / Input
    case ErrorCodes.INVALID_INPUT:
      return 'Invalid input. Please check your entries.';

    // 🚫 Forbidden
    case ErrorCodes.FORBIDDEN:
      return "You don't have permission to perform this action.";

    // 💥 Server
    case ErrorCodes.INTERNAL_ERROR:
      return 'Internal server error. Please try again later.';

    // 🧩 Application Logic
    case ErrorCodes.BAD_REQUEST:
      return 'Invalid request. Please check the data and try again.';

    case ErrorCodes.NOT_FOUND:
      return 'Requested resource not found.';

    case ErrorCodes.DUPLICATE:
      return 'Duplicate entry detected.';

    // 💾 Database
    case ErrorCodes.DB_ERROR:
      return 'Database error occurred. Please try again later.';

    // 🌐 External Service
    case ErrorCodes.EXTERNAL_SERVICE_ERROR:
      return 'External service unavailable. Please try again later.';

    default:
      // Fallback based on HTTP status if code not found
      if (status === 401) {
        return 'Unauthorized access. Please log in.';
      }
      if (status === 403) return 'Forbidden request.';
      if (status === 404) return 'Resource not found.';
      if (status === 500) return 'Internal server error.';

      return error.message || 'An unknown error occurred.';
  }
};
